/**
 * Video Reels Carousel Component
 *
 * A horizontally scrollable carousel of vertical (9:16) video cards with:
 * - CSS scroll-snap for smooth swiping
 * - IntersectionObserver-based autoplay (muted) when visible
 * - Tap/click to play/pause
 * - Mute/unmute toggle
 * - Navigation arrows and pagination dots
 * - Infinite loop option
 * - Carousel autoplay (auto-advance)
 * - Keyboard accessibility
 * - Videos load only once the page stops moving, so scrolling past costs nothing
 */

import { Component } from '@theme/component';
import { isSlowConnection, prefersReducedMotion } from '@theme/utilities';

/** How long the page has to stay still, in ms, before a video starts. */
const SETTLE_DELAY = 500;

/** The longest a video waits, in ms, for the one before it to load enough to play through. */
const START_GAP = 4000;

/** The same wait on a slow connection, where a few videos at once leave nothing for the images. */
const SLOW_START_GAP = 20000;

class VideoReelsComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;

  /** @type {Set<HTMLElement>} On-screen cards waiting for the page to stop moving */
  #waitingCards = new Set();

  /** @type {number | undefined} */
  #videoTimer;

  /** @type {number | undefined} */
  #autoAdvanceTimer;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {boolean} */
  #isUserInteracting = false;

  /** @type {number | null} Index of the card whose audio is currently unmuted, or null if all muted */
  #activeAudioIndex = null;

  /** @type {boolean} */
  #isScrollingProgrammatically = false;

  /** @type {(() => void) | undefined} Ends the #goToSlide() scroll in flight, if any */
  #settleScroll;

  /** @type {IntersectionObserver | undefined} */
  #inViewObserver;

  /** @type {boolean} */
  #isInView = false;

  /** @type {boolean} Guards against re-firing #navigate() from one continuous overscroll gesture */
  #boundaryBounceLocked = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;

    // Cache refs
    this._track = this.querySelector('[data-reels-track]');
    this._cards = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-reel-card]')]);
    this._dots = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-reel-dot]')]);
    this._prevBtn = this.querySelector('[data-reels-prev]');
    this._nextBtn = this.querySelector('[data-reels-next]');
    this._counter = this.querySelector('[data-reels-counter]');

    if (!this._track || this._cards.length === 0) return;

    // ---- Keyboard navigation ----
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.setAttribute('tabindex', '0');

    // ---- Pause carousel autoplay on hover/focus ----
    this.addEventListener('mouseenter', () => { this.#isUserInteracting = true; this.#stopAutoAdvance(); }, { signal });
    this.addEventListener('mouseleave', () => { this.#isUserInteracting = false; this.#startAutoAdvance(); }, { signal });
    this.addEventListener('focusin', () => { this.#isUserInteracting = true; this.#stopAutoAdvance(); }, { signal });
    this.addEventListener('focusout', () => { this.#isUserInteracting = false; this.#startAutoAdvance(); }, { signal });

    // ---- Track scroll events for snap-based index tracking ----
    let scrollTimeout;
    this._track.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.#onScrollEnd(), 100);
    }, { signal, passive: true });

    // ---- Infinite loop: detect an overscroll gesture pinned at the track's
    // start/end edge and wrap via the existing #navigate(), since native
    // scroll-snap alone just stops at the boundary. ----
    let wheelOverscroll = 0;
    let wheelResetTimer;
    this._track.addEventListener('wheel', (event) => {
      if (this.dataset.infinite !== 'true') return;
      if (this.#isScrollingProgrammatically || this.#boundaryBounceLocked) return;

      const delta = event.deltaX !== 0 ? event.deltaX : event.deltaY;
      if (delta === 0) return;

      const track = this._track;
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      const atEnd = track.scrollLeft >= maxScrollLeft - 1;
      const atStart = track.scrollLeft <= 1;

      if ((delta > 0 && atEnd) || (delta < 0 && atStart)) {
        wheelOverscroll += delta;
        clearTimeout(wheelResetTimer);
        wheelResetTimer = setTimeout(() => { wheelOverscroll = 0; }, 250);

        const WHEEL_THRESHOLD = 60;
        if (Math.abs(wheelOverscroll) > WHEEL_THRESHOLD) {
          const direction = wheelOverscroll > 0 ? 1 : -1;
          wheelOverscroll = 0;
          this.#triggerBoundaryBounce(direction);
        }
      } else {
        wheelOverscroll = 0;
      }
    }, { signal, passive: true });

    let touchStartX = 0;
    let touchPinnedEdge = 0; // 1 = pinned at end, -1 = pinned at start, 0 = not pinned
    this._track.addEventListener('touchstart', (event) => {
      if (this.dataset.infinite !== 'true') return;
      const track = this._track;
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      touchPinnedEdge = track.scrollLeft >= maxScrollLeft - 1 ? 1 : track.scrollLeft <= 1 ? -1 : 0;
      touchStartX = event.touches[0].clientX;
    }, { signal, passive: true });

    this._track.addEventListener('touchend', (event) => {
      if (this.dataset.infinite !== 'true' || !touchPinnedEdge) return;
      if (this.#isScrollingProgrammatically || this.#boundaryBounceLocked) return;

      const track = this._track;
      const endX = event.changedTouches[0].clientX;
      const deltaX = touchStartX - endX; // positive = swiped toward "next"
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      const stillPinnedSameEdge =
        (touchPinnedEdge === 1 && track.scrollLeft >= maxScrollLeft - 1) ||
        (touchPinnedEdge === -1 && track.scrollLeft <= 1);

      const TOUCH_SWIPE_THRESHOLD = 30;
      if (stillPinnedSameEdge && Math.abs(deltaX) > TOUCH_SWIPE_THRESHOLD) {
        if (touchPinnedEdge === 1 && deltaX > 0) this.#triggerBoundaryBounce(1);
        else if (touchPinnedEdge === -1 && deltaX < 0) this.#triggerBoundaryBounce(-1);
      }
      touchPinnedEdge = 0;
    }, { signal, passive: true });

    // ---- Visibility observer: autoplay/pause videos ----
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const card = /** @type {HTMLElement} */ (entry.target);
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            if (!prefersReducedMotion()) this.#queueVideo(card);
          } else {
            this.#waitingCards.delete(card);
            this.#pauseVideo(card);
            this.#stopDownload(card);
          }
        }
      },
      { threshold: [0, 0.6] }
    );

    // Observe all cards
    this._cards.forEach((card) => this.#visibilityObserver?.observe(card));

    // Captured so sliding the track counts as well as scrolling the page
    document.addEventListener('scroll', this.#handleScroll, { signal, capture: true, passive: true });

    // ---- Document visibility ----
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._cards.forEach((card) => this.#pauseVideo(card));
        this.#stopAutoAdvance();
      } else {
        this.#startAutoAdvance();
      }
    }, { signal });

    // Initial state
    this.#updateControls();
    this.#updatePlaybackStates(); // Dynamically updates mute icons/states on load

    // ---- Carousel autoplay only runs while the carousel is on screen ----
    if (this.dataset.carouselAutoplay === 'true') {
      this.#inViewObserver = new IntersectionObserver(([entry]) => {
        this.#isInView = Boolean(entry?.isIntersecting);
        if (this.#isInView) this.#startAutoAdvance();
        else this.#stopAutoAdvance();
      });
      this.#inViewObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#visibilityObserver?.disconnect();
    clearTimeout(this.#videoTimer);
    this.#inViewObserver?.disconnect();
    this.#stopAutoAdvance();
  }

  // =============== NATIVE DELEGATED EVENT HANDLERS ===============

  navigatePrev(event) {
    event.stopPropagation();
    this.#navigate(-1);
  }

  navigateNext(event) {
    event.stopPropagation();
    this.#navigate(1);
  }

  goToSlideFromDot(data, event) {
    event.stopPropagation();
    const index = parseInt(data.index, 10);
    if (!isNaN(index)) {
      this.#goToSlide(index);
    }
  }

  toggleMute(event) {
    event.stopPropagation();
    const card = event.target.closest('[data-reel-card]');
    if (!card) return;

    // Toggle this specific card's audio without moving the carousel.
    // Unmuting a card automatically mutes whichever other card was active.
    this.#toggleMute(card);
  }

  togglePlayPauseOnCard(event) {
    // Avoid triggering when clicking links or the mute toggle button
    if (event.target.closest('a, [data-reel-mute-toggle]')) return;
    event.stopPropagation();
    const card = event.target.closest('[data-reel-card]');
    if (card) {
      const index = this._cards.indexOf(card);
      if (index !== -1) {
        if (index !== this.#currentIndex) {
          this.#goToSlide(index);
        } else {
          this.#togglePlayPause(card);
        }
      }
    }
  }

  // =============== NAVIGATION ===============

  /**
   * Wraps to the opposite end in response to a wheel/touch gesture that
   * overscrolls past a pinned track boundary (see the 'wheel'/'touchend'
   * listeners in connectedCallback). Locks briefly so trailing events from
   * the same gesture, or the scroll settling from #goToSlide() itself,
   * can't double-trigger.
   * @param {1 | -1} direction
   */
  #triggerBoundaryBounce(direction) {
    this.#boundaryBounceLocked = true;
    this.#navigate(direction);
    setTimeout(() => {
      this.#boundaryBounceLocked = false;
    }, 700);
  }

  /** @param {number} direction - -1 for prev, 1 for next */
  #navigate(direction) {
    const infinite = this.dataset.infinite === 'true';
    const total = this._cards.length;
    let newIndex = this.#currentIndex + direction;

    if (infinite) {
      if (newIndex < 0) newIndex = total - 1;
      if (newIndex >= total) newIndex = 0;
    } else {
      newIndex = Math.max(0, Math.min(newIndex, total - 1));
    }

    this.#goToSlide(newIndex);
  }

  /** @param {number} index */
  #goToSlide(index) {
    if (!this._track || !this._cards[index]) return;
    this.#settleScroll?.();
    this.#currentIndex = index;
    this.#isScrollingProgrammatically = true;

    // Temporarily disable scroll snap and smooth scrolling to prevent browser conflicts
    this._track.style.scrollSnapType = 'none';
    this._track.style.scrollBehavior = 'auto';

    const trackRect = this._track.getBoundingClientRect();
    const cardRect = this._cards[index].getBoundingClientRect();
    const scrollLeft = this._track.scrollLeft + (cardRect.left - trackRect.left);

    this._track.scrollTo({
      left: scrollLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });

    this.#updateControls();
    this.#updatePlaybackStates();

    // Re-enable scroll snap after scroll animation ends. The timeout covers browsers without
    // `scrollend` and scrolls that go nowhere, which never fire it.
    const track = this._track;
    const settle = () => {
      clearTimeout(timeout);
      track.removeEventListener('scrollend', settle);
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
      this.#isScrollingProgrammatically = false;
      this.#settleScroll = undefined;
    };
    const timeout = setTimeout(settle, 500);
    track.addEventListener('scrollend', settle);
    this.#settleScroll = settle;
  }

  #onScrollEnd() {
    if (!this._track || this.#isScrollingProgrammatically) return;

    const trackRect = this._track.getBoundingClientRect();

    let closestIndex = 0;
    let closestDistance = Infinity;

    // Cards snap to the track's start edge (scroll-snap-align: start), and
    // #goToSlide() aligns the same way — so "closest" must be measured against
    // the start edge too. Comparing centers instead (as before) picks a
    // different card whenever more than one is visible per view, which made
    // the index silently drift after every navigation and broke the infinite
    // wraparound (0 would land back on a later card instead).
    this._cards.forEach((card, i) => {
      const cardRect = card.getBoundingClientRect();
      const distance = Math.abs(cardRect.left - trackRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    });

    this.#currentIndex = closestIndex;
    this.#updateControls();
    this.#updatePlaybackStates();
  }

  #updateControls() {
    const infinite = this.dataset.infinite === 'true';
    const total = this._cards.length;

    // Arrows
    if (this._prevBtn) {
      this._prevBtn.toggleAttribute('disabled', !infinite && this.#currentIndex <= 0);
    }
    if (this._nextBtn) {
      this._nextBtn.toggleAttribute('disabled', !infinite && this.#currentIndex >= total - 1);
    }

    // Dots
    this._dots.forEach((dot, i) => {
      dot.classList.toggle('video-reels__dot--active', i === this.#currentIndex);
      dot.setAttribute('aria-current', i === this.#currentIndex ? 'true' : 'false');
    });

    // Counter
    if (this._counter) {
      this._counter.textContent = `${this.#currentIndex + 1} / ${total}`;
    }
  }

  // =============== CAROUSEL AUTOPLAY (auto-advance) ===============

  #startAutoAdvance() {
    if (this.dataset.carouselAutoplay !== 'true' || this.#isUserInteracting || !this.#isInView) return;
    if (prefersReducedMotion()) return;
    this.#stopAutoAdvance();
    const speed = parseInt(this.dataset.carouselSpeed || '5', 10) * 1000;
    this.#autoAdvanceTimer = window.setInterval(() => {
      this.#navigate(1);
    }, speed);
  }

  #stopAutoAdvance() {
    if (this.#autoAdvanceTimer) {
      clearInterval(this.#autoAdvanceTimer);
      this.#autoAdvanceTimer = undefined;
    }
  }

  // =============== VIDEO CONTROL ===============

  /**
   * Starting a video is heavy and several cards can show at once, so a card
   * waits until the page has stopped moving.
   * @param {HTMLElement} card
   */
  #queueVideo(card) {
    this.#waitingCards.add(card);
    this.#restartVideoTimer();
  }

  #handleScroll = () => {
    if (this.#waitingCards.size > 0) this.#restartVideoTimer();
  };

  #restartVideoTimer() {
    clearTimeout(this.#videoTimer);
    this.#videoTimer = window.setTimeout(this.#playNextVideo, SETTLE_DELAY);
  }

  #playNextVideo = () => {
    const [card] = this.#waitingCards;
    if (!card) return;

    this.#waitingCards.delete(card);
    this.#playVideo(card);

    // Loading them all at once leaves a slow connection unable to play any of them
    const video = card.querySelector('video');
    if (video && video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
      video.addEventListener('canplaythrough', () => this.#restartVideoTimer(), { once: true });
      this.#videoTimer = window.setTimeout(this.#playNextVideo, isSlowConnection() ? SLOW_START_GAP : START_GAP);
    } else {
      this.#restartVideoTimer();
    }
  };

  /** @param {HTMLElement} card */
  #loadVideo(card) {
    const template = card.querySelector('template[data-video-template]');
    if (!template || card.dataset.videoLoaded === 'true') return;

    const content = /** @type {DocumentFragment} */ (
      /** @type {HTMLTemplateElement} */ (template).content.cloneNode(true)
    );
    const video = content.querySelector('video');
    // Playback is left to the visibility observer, so off-screen cards don't play or spend data
    video?.removeAttribute('autoplay');

    // The poster stays up until there's a moving picture to replace it
    const showVideo = () => { card.dataset.videoStarted = 'true'; };
    video?.addEventListener('playing', showVideo, { once: true });
    content.querySelector('iframe')?.addEventListener('load', showVideo, { once: true });

    card.querySelector('[data-video-container]')?.appendChild(content);
    card.dataset.videoLoaded = 'true';
  }

  /**
   * A paused video keeps downloading, which on a slow connection starves the images further down
   * the page, so one that hasn't finished is taken out and comes back when its card does.
   * @param {HTMLElement} card
   */
  #stopDownload(card) {
    const video = card.querySelector('video');
    if (!video) return;

    const { buffered, duration } = video;
    if (buffered.length > 0 && buffered.end(buffered.length - 1) >= duration) return;

    video.querySelectorAll('source').forEach((source) => source.remove());
    video.removeAttribute('src');
    video.load();
    video.remove();
    delete card.dataset.videoLoaded;
    delete card.dataset.videoStarted;
  }

  /** @param {HTMLElement} card */
  #playVideo(card) {
    // Ensure video is loaded first
    if (card.dataset.videoLoaded !== 'true') {
      this.#loadVideo(card);
    }

    const video = card.querySelector('video');
    if (video) {
      // Unmute only if this card is the one with active audio
      const index = this._cards.indexOf(card);
      video.muted = this.#activeAudioIndex !== index;

      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch((error) => {
          // Pausing a video that's still loading lands here too, and it has to stay paused
          if (error.name !== 'NotAllowedError') return;

          // Autoplay was prevented — try muted
          video.muted = true;
          video.play().catch(() => {});
        });
      }
      card.dataset.playing = 'true';
      this.#updatePlayPauseIcon(card, true);
    }

    // For iframes (YouTube/Vimeo), send play command
    const iframe = card.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.dataset.videoType;
      if (type === 'youtube') {
        iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      } else if (type === 'vimeo') {
        iframe.contentWindow?.postMessage('{"method":"play"}', '*');
      }

      // Sync mute state to the autoplaying iframe
      const index = this._cards.indexOf(card);
      this.#setCardMuteState(card, this.#activeAudioIndex !== index);

      card.dataset.playing = 'true';
      this.#updatePlayPauseIcon(card, true);
    }
  }

  /** @param {HTMLElement} card */
  #pauseVideo(card) {
    const video = card.querySelector('video');
    if (video) {
      video.pause();
      card.dataset.playing = 'false';
      this.#updatePlayPauseIcon(card, false);
    }

    const iframe = card.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.dataset.videoType;
      if (type === 'youtube') {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (type === 'vimeo') {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
      card.dataset.playing = 'false';
      this.#updatePlayPauseIcon(card, false);
    }
  }

  /** @param {HTMLElement} card */
  #togglePlayPause(card) {
    if (card.dataset.playing === 'true') {
      this.#pauseVideo(card);
    } else {
      this.#playVideo(card);
    }
  }

  /**
   * Sets the mute state of a card (both HTML5 video and iframes)
   * @param {HTMLElement} card
   * @param {boolean} shouldMute
   */
  #setCardMuteState(card, shouldMute) {
    const video = card.querySelector('video');
    if (video) {
      video.muted = shouldMute;
    }

    const iframe = card.querySelector('iframe[data-video-type]');
    if (iframe) {
      const type = iframe.dataset.videoType;
      if (type === 'youtube') {
        const func = shouldMute ? 'mute' : 'unmute';
        iframe.contentWindow?.postMessage(`{"event":"command","func":"${func}","args":""}`, '*');
      } else if (type === 'vimeo') {
        const method = shouldMute ? 'mute' : 'unmute';
        iframe.contentWindow?.postMessage(`{"method":"${method}"}`, '*');
      }
    }

    // Update mute icon visual state on this specific card
    const muteBtn = card.querySelector('[data-reel-mute-toggle]');
    if (muteBtn) {
      const volIcon = muteBtn.querySelector('[data-icon-volume]');
      const muteIcon = muteBtn.querySelector('[data-icon-muted]');
      if (volIcon) volIcon.classList.toggle('hidden', shouldMute);
      if (muteIcon) muteIcon.classList.toggle('hidden', !shouldMute);
    }
  }

  /**
   * Ensures only the card with active audio plays sound,
   * while every other card is strictly muted.
   */
  #updatePlaybackStates() {
    this._cards.forEach((card, index) => {
      const shouldMute = this.#activeAudioIndex !== index;
      this.#setCardMuteState(card, shouldMute);
    });
  }

  /** @param {HTMLElement} card */
  #toggleMute(card) {
    const index = this._cards.indexOf(card);
    if (index === -1) return;
    this.#activeAudioIndex = this.#activeAudioIndex === index ? null : index;
    this.#updatePlaybackStates();
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} isPlaying
   */
  #updatePlayPauseIcon(card, isPlaying) {
    const btn = card.querySelector('[data-reel-play-toggle]');
    if (!btn) return;
    const playIcon = btn.querySelector('[data-icon-play]');
    const pauseIcon = btn.querySelector('[data-icon-pause]');
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
  }

  // =============== KEYBOARD ===============

  /** @param {KeyboardEvent} e */
  #handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.#navigate(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.#navigate(1);
        break;
      case ' ':
      case 'Enter': {
        // Only the carousel itself: its buttons and links need these keys for their own clicks
        if (e.target !== this) break;
        e.preventDefault();
        const activeCard = this._cards[this.#currentIndex];
        if (activeCard) this.#togglePlayPause(activeCard);
        break;
      }
      case 'm':
      case 'M': {
        const activeCard = this._cards[this.#currentIndex];
        if (activeCard) this.#toggleMute(activeCard);
        break;
      }
    }
  };
}

if (!customElements.get('video-reels-component')) {
  customElements.define('video-reels-component', VideoReelsComponent);
}